export type ScrollDirection = -1 | 0 | 1

export interface LandingMotionState {
  progress: number
  section: number
  sectionIndex: number
  localProgress: number
  easedLocal: number
  velocity: number
  direction: ScrollDirection
}

export interface LandingMotionRef {
  current: LandingMotionState
}

export const SECTION_COUNT = 8
export const LAST_SECTION = SECTION_COUNT - 1

export function clamp(value: number, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value))
}

export function smoothstep(edge0: number, edge1: number, value: number) {
  const x = clamp((value - edge0) / Math.max(0.0001, edge1 - edge0))
  return x * x * (3 - 2 * x)
}

export function easeOutCubic(value: number) {
  const x = clamp(value)
  return 1 - Math.pow(1 - x, 3)
}

export function sectionFromProgress(progress: number) {
  return clamp(progress, 0, 1) * LAST_SECTION
}

export function buildLandingMotion(progress: number, velocity = 0, direction: ScrollDirection = 0): LandingMotionState {
  const section = sectionFromProgress(progress)
  const sectionIndex = Math.min(LAST_SECTION, Math.max(0, Math.round(section)))
  const localProgress = clamp(section - Math.floor(section))

  return {
    progress: clamp(progress),
    section,
    sectionIndex,
    localProgress,
    easedLocal: easeOutCubic(localProgress),
    velocity,
    direction,
  }
}

export function scenePresence(section: number, start: number, end: number, feather = 0.32) {
  const enter = smoothstep(start - feather, start + feather, section)
  const exit = 1 - smoothstep(end - feather, end + feather, section)
  return clamp(enter * exit)
}

export function sectionFocus(section: number, center: number, radius = 0.72) {
  return clamp(1 - Math.abs(section - center) / radius)
}

export function pulse(section: number, center: number, radius = 0.38) {
  const focus = sectionFocus(section, center, radius)
  return Math.sin(focus * Math.PI)
}
